import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { startWith, distinctUntilChanged, map } from 'rxjs/operators';
import { Company } from 'src/app/core/interfaces/company';
import { CostCenter } from 'src/app/core/interfaces/costCenter';
import { PermissionRole } from 'src/app/core/resources/permission-role';
import { Permission } from 'src/app/core/resources/permission';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ModalEnum } from 'src/app/core/enums/modal.enum';
import { CostCenterFormComponent } from '../cost-center-form/cost-center-form.component';
import { Cargo } from 'src/app/core/interfaces/cargo';
import { CompaniesService } from '../list-companies.service';

@Component({
  selector: 'app-assign-cost-center',
  templateUrl: './assign-cost-center.component.html',
  styleUrls: ['./assign-cost-center.component.scss']
})
export class AssignCostCenterComponent implements OnInit {
  // Inputs
  @Input() company: Company;
  @Input() initialValue: CostCenter;
  @Input() cargo: Cargo;
  // Empresa de transporte del servicio: el centro creado aquí se asocia para ella.
  @Input() transportCompanyId: string;
  // Outputs
  @Output() emitResult: EventEmitter<{costCenter: CostCenter, created: boolean} | null> = new EventEmitter();
  public costCenterCtrl: FormControl = new FormControl('');
  public listGeneralCostCenter: Observable<CostCenter[]>;
  public costCenters: CostCenter[] = [];
  permission = Permission;
  // Última consulta de asociados: si cambia la empresa mientras responde, se descarta la anterior.
  private costCentersRequestKey: string = null;

  constructor(
    public permissionRole: PermissionRole,
    public dialog: MatDialog,
    private companiesService: CompaniesService
  ) { }

  ngOnInit() {}
  ngOnChanges(changes: SimpleChanges) {
    if (changes){
      if (changes.initialValue && changes.initialValue.currentValue && this.initialValue) this.costCenterCtrl.setValue(this.initialValue);
      if ((changes.company || changes.transportCompanyId) && this.company && this.company.companyId)
        this.loadCostCenters();
    }
  }

  /** Centros asociados a la empresa cliente para la empresa de transporte del servicio (GET company/{nit}/cost-centers). */
  private async loadCostCenters(): Promise<void> {
    const requestKey = `${this.company.companyId}|${this.transportCompanyId || ''}`;
    if (requestKey === this.costCentersRequestKey) return;
    this.costCentersRequestKey = requestKey;
    let costCenters: CostCenter[] = [];
    try {
      costCenters = await this.companiesService.getCompanyCostCenters(this.company.companyId, this.transportCompanyId).toPromise() || [];
    } catch (error) {
      costCenters = [];
    }
    if (requestKey !== this.costCentersRequestKey) return;
    this.costCenters = costCenters;
    if (this.costCenters.length) {
      this.costCenterCtrl.enable();
      if (this.cargo && this.cargo.shippingCost && this.cargo.shippingCost.billId) this.costCenterCtrl.disable();
    } else {
      this.costCenterCtrl.setValue('');
      this.costCenterCtrl.disable();
    }
    this.createInstanceAutocomplete();
  }

  private createInstanceAutocomplete(): void {
      this.listGeneralCostCenter = this.costCenterCtrl.valueChanges.pipe(
        startWith(''),
        distinctUntilChanged(),
        map(value => {
          return this.costCenters.filter((option) => {
            return option.name.toLowerCase().indexOf(value.toString()) !== -1;
          });
        })
      );
  }

  public displayCostCenterSelected(costCenter: CostCenter): string {
    return costCenter ? costCenter.name || '' : '';
  }

  onCostCenterSelected($event: MatAutocompleteSelectedEvent){
    if ($event && $event.option && $event.option.value && $event.option.value.code) this.emitResult.emit({costCenter: $event.option.value, created: false});
  }

  openCreateCostCenter() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      nitCompany: this.company.companyId,
      nameCompany: this.company.name,
      transportCompanyId: this.transportCompanyId
    };
    dialogConfig.maxHeight = ModalEnum.MAX_HEIGHT;
    dialogConfig.width = ModalEnum.EXTRA_SMALL_WIDTH;
    dialogConfig.maxWidth = ModalEnum.MAX_WIDTH;
    dialogConfig.autoFocus = false;
    const dialogRef = this.dialog.open(CostCenterFormComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      if (result && result.state && result.data) {
        this.costCenterCtrl.setValue(result.data);
        // La respuesta trae la lista actualizada de asociados; si no, se agrega el creado.
        if (result.costCenters) this.costCenters = result.costCenters;
        else this.costCenters.push(result.data);
        if (this.costCenterCtrl.disabled) this.costCenterCtrl.enable();
        this.emitResult.emit({costCenter: result.data, created: true});
      }
    });
  }

  clearCostCenter(){
    this.costCenterCtrl.setValue('');
    this.emitResult.emit(null);
  }

  // GETTERS
  get canCreateCostCenter(): boolean {
    return this.permissionRole.hasPermission(
      this.permission.payments.module,
      this.permission.payments.createCostCenter
    );
  }

}
